<template>
  <div :class="['institution-application', isEnglish ? 'english-page' : 'chinese-page', `step-${step}`]">
    <header class="application-header">
      <div class="application-header-inner">
        <a :href="homePath" class="application-logo">
          <img v-if="isEnglish" src="~@/assets/institution-application/logo-en.svg" alt="CF40 RESEARCH" />
          <img v-else src="~@/assets/institution-application/logo-cn.svg" alt="CF40 研究" />
        </a>
        <img v-if="isEnglish" class="application-slogan" src="~@/assets/institution-application/slogan-en.svg"
          alt="Independent research on China, for the world’s decision-makers." />
      </div>
    </header>

    <main class="application-main">
      <div class="application-content">
        <section class="benefits-panel">
          <h1 v-if="isEnglish">FREE TRIAL</h1>
          <p v-else class="benefits-intro">开通试用账号，您可获得以下权益：</p>
          <div class="benefit-list">
            <div v-for="item in benefits" :key="item.title" class="benefit-item">
              <img class="benefit-icon" :src="item.icon" alt="" aria-hidden="true" />
              <div>
                <strong>{{ item.title }}</strong><span v-if="!isEnglish">：</span>
                <p>{{ item.description }}</p>
              </div>
            </div>
          </div>
        </section>

        <section class="form-panel">
          <h2 v-if="!isEnglish">试用申请</h2>
          <div v-loading="verifying"
            :class="['application-card', { 'has-status': applicationStatus || $route.query.review === '1' }]">
            <el-form v-if="step === 1" ref="emailForm" class="email-form" hide-required-asterisk :model="emailForm"
              :rules="emailRules" label-position="top" @submit.native.prevent>
              <div v-if="$route.query.review === '1'" class="status-panel">
                <strong>{{ isEnglish ? 'Check Application Status' : '查看申请结果' }}</strong>
                <p>{{ copy.reviewDescription }}</p>
              </div>
              <el-form-item :label="copy.emailLabel" prop="email" :error="emailError">
                <template slot="label">{{ copy.emailLabel.replace(/\*$/, '') }}<span
                    class="required-mark">*</span></template>
                <el-input v-model.trim="emailForm.email" :disabled="verifying || sendingCode"
                  :placeholder="copy.emailPlaceholder" @blur="checkApplicationEmail" @input="emailError = ''" />
              </el-form-item>
              <el-form-item :label="copy.codeLabel" prop="verification_code">
                <template slot="label">{{ copy.codeLabel.replace(/\*$/, '') }}<span
                    class="required-mark">*</span></template>
                <div class="code-field">
                  <el-input v-model.trim="emailForm.verification_code" maxlength="4" :disabled="verifying"
                    :placeholder="copy.codePlaceholder" @keyup.enter.native="continueApplication" />
                  <button type="button" class="code-button" :disabled="sendingCode || countdown > 0" @click="sendCode">
                    {{ countdown > 0 ? `${countdown}s` : copy.getCode }}
                  </button>
                </div>
              </el-form-item>
              <el-button :class="['primary-button', { 'is-ready': canContinue }]" :loading="verifying"
                @click="continueApplication">
                {{ copy.continue }}
              </el-button>
              <p class="form-hint">{{ copy.contactHint }}</p>
            </el-form>

            <el-form v-else ref="applicationForm" class="details-form" hide-required-asterisk :model="applicationForm"
              :rules="applicationRules" label-position="top" @submit.native.prevent>
              <div v-if="applicationStatus" :class="['status-panel', statusClass]">
                <strong>{{ statusTitle }}</strong>
                <p>{{ statusDescription }}</p>
                <p v-if="applicationStatus === 'PENDING'">{{ copy.pendingContact }}
                  <a class="contact-email" href="mailto:cf40research@cf40.org.cn">cf40research@cf40.org.cn</a>
                </p>
              </div>
              <div class="form-grid">
                <el-form-item :label="copy.nameLabel" prop="contact_name">
                  <template slot="label">{{ copy.nameLabel.replace(/\*$/, '') }}<span
                      class="required-mark">*</span></template>
                  <el-input v-model.trim="applicationForm.contact_name" :disabled="readOnly"
                    :placeholder="copy.namePlaceholder" />
                </el-form-item>
                <el-form-item :label="copy.institutionLabel" prop="institution_name">
                  <template slot="label">{{ copy.institutionLabel.replace(/\*$/, '') }}<span
                      class="required-mark">*</span></template>
                  <el-input v-model.trim="applicationForm.institution_name" :disabled="readOnly"
                    :placeholder="copy.institutionPlaceholder" />
                </el-form-item>
                <el-form-item :label="copy.dutyLabel" prop="contact_duty">
                  <template slot="label">{{ copy.dutyLabel.replace(/\*$/, '') }}<span
                      class="required-mark">*</span></template>
                  <el-input v-model.trim="applicationForm.contact_duty" :disabled="readOnly"
                    :placeholder="copy.dutyPlaceholder" />
                </el-form-item>
                <el-form-item :label="copy.typeLabel" prop="institution_type">
                  <template slot="label">{{ copy.typeLabel.replace(/\*$/, '') }}<span
                      class="required-mark">*</span></template>
                  <el-select v-model="applicationForm.institution_type" :disabled="readOnly"
                    :placeholder="copy.typePlaceholder">
                    <el-option v-for="item in institutionTypes" :key="item.value" :label="item.label"
                      :value="item.value" />
                  </el-select>
                </el-form-item>
                <el-form-item class="location-field" :label="copy.locationLabel" prop="location">
                  <template slot="label">{{ copy.locationLabel.replace(/\*$/, '') }}<span
                      class="required-mark">*</span></template>
                  <el-cascader v-model="applicationForm.location" :options="regionOptions" :props="cascaderProps"
                    :disabled="readOnly" filterable clearable :placeholder="copy.locationPlaceholder"
                    @change="handleLocationChange" />
                </el-form-item>
              </div>
              <div v-if="applicationStatus === 'REJECTED'" class="reject-reason">
                <strong>{{ copy.rejectReason }}</strong>
                <p>{{ reviewRemark || copy.rejectReasonFallback }}</p>
              </div>
              <el-button v-if="!readOnly" class="primary-button" :loading="submitting" @click="submitApplication">
                {{ applicationStatus === 'REJECTED' ? copy.submitAgain : copy.submit }}
              </el-button>
            </el-form>
            <div v-if="step === 2" class="agreement-consent" :aria-busy="agreementLoading">
              <el-checkbox v-if="!readOnly" v-model="agreementsAccepted"
                :aria-label="isEnglish ? 'I agree to the Membership Service Agreement and Privacy Policy' : '我已阅读并同意会员服务协议和隐私政策'">
                {{ isEnglish ? 'I agree to the' : '我已阅读并同意本网站的' }}
              </el-checkbox>
              <span v-else>{{ isEnglish ? 'Please read our ' : '请阅读本网站的' }}</span>
              <button type="button" :disabled="agreementLoading" @click="openAgreement('membership')">{{ isEnglish ?
                'Membership Service Agreement' : '《会员服务协议》' }}</button>
              <span>{{ isEnglish ? ' and ' : '和' }}</span>
              <button type="button" :disabled="agreementLoading" @click="openAgreement('privacy')">{{ isEnglish ?
                'Privacy Policy' :
                '《隐私政策》' }}</button><span>{{ isEnglish ? '.' : '。' }}</span>
              <span v-if="agreementLoading" class="agreement-loading" role="status">{{ isEnglish ? 'Loading…' : '加载中…'
                }}</span>
            </div>
          </div>
          <button type="button" class="cancel-link" @click="goHome">
            {{ readOnly ? copy.returnHome : copy.cancel }}
          </button>
        </section>
      </div>
    </main>

    <service-agreement ref="serviceAgreementRef" />
    <el-dialog :visible.sync="successVisible" custom-class="application-success-dialog"
      modal-class="application-success-modal" :modal-append-to-body="false"
      :title="isEnglish ? 'Thank You For Your Application' : '试用申请已提交'" width="322px" top="0" :show-close="false"
      :close-on-click-modal="false">
      <img class="success-icon" src="~@/assets/institution-application/success.svg" alt="" />
      <h3>{{ isEnglish ? 'Thank You For Your Application' : '试用申请已提交' }}</h3>
      <p>{{ copy.pendingDescription }}</p>
      <el-button class="primary-button" @click="successVisible = false">{{ isEnglish ? 'Done' : '完成' }}</el-button>
    </el-dialog>
    <footer class="application-footer">
      <div class="footer-contact">
        <span>{{ isEnglish ? 'Tel:' : '联系电话:' }}010-58297189</span>
        <span>{{ isEnglish ? 'Mail: ' : '邮箱: ' }}<a
            href="mailto:cf40research@cf40.org.cn">cf40research@cf40.org.cn</a></span>
        <span>京ICP备2023034734号-1</span>
        <span>京公网安备11010202011065号</span>
      </div>
      <p>{{ isEnglish ? 'Copyright Beijing Forty People Information Technology Co., Ltd. 2024-2025' :
        'Copyright 2024 - 2025北京四十人信息科技有限公司版权所有' }}</p>
    </footer>
  </div>
</template>

<script>
import ServiceAgreement from '@/components/serviceAgreement'
import researchIcon from '@/assets/institution-application/research.svg'
import bilingualIcon from '@/assets/institution-application/bilingual.svg'
import platformIcon from '@/assets/institution-application/platform.svg'
import advisoryIcon from '@/assets/institution-application/advisory.svg'
import locale from 'element-ui/lib/locale'
import enLocale from 'element-ui/lib/locale/lang/en'
import zhLocale from 'element-ui/lib/locale/lang/zh-CN'
import {
  initConfig,
  currentInstitutionApplication,
  validateInstitutionApplicationEmail,
  institutionApplicationRegions,
  institutionApplicationTypes,
  sendInstitutionApplicationCode,
  submitInstitutionApplication,
  verifyInstitutionApplicationCode
} from '@/api'

export default {
  name: 'InstitutionApplication',
  components: { ServiceAgreement },
  data() {
    return {
      step: 1,
      successVisible: false,
      agreementsAccepted: false,
      agreementLoading: false,
      sendingCode: false,
      verifying: false,
      submitting: false,
      requiresEmailVerification: false,
      countdown: 0,
      countdownTimer: null,
      verificationToken: '',
      applicationStatus: '',
      reviewRemark: '',
      regionOptions: [],
      institutionTypeOptions: [],
      cascaderProps: { value: 'value', label: 'label', children: 'children', emitPath: true },
      emailError: '',
      emailForm: { email: '', verification_code: '' },
      applicationForm: {
        contact_name: '', institution_name: '', contact_duty: '', institution_type: '',
        location: [], country_region: '', province: '', city: ''
      }
    }
  },
  computed: {
    isEnglish() {
      return this.$route.params.language === 'english'
    },
    canContinue() {
      return (this.isLoggedIn && !this.requiresEmailVerification) || Boolean(this.emailForm.email && this.emailForm.verification_code)
    },
    homePath() {
      return this.isEnglish ? '/en' : '/'
    },
    copy() {
      if (this.isEnglish) {
        return {
          emailLabel: 'Business email for verification*', emailPlaceholder: 'Enter verification email',
          codeLabel: 'Enter verification code*', codePlaceholder: 'Enter verification code',
          getCode: 'Get Code', continue: 'Continue',
          contactHint: '',
          nameLabel: 'Full Name*', namePlaceholder: 'Enter your full name',
          institutionLabel: 'Company / Institution Name*', institutionPlaceholder: 'Enter company/institution name',
          dutyLabel: 'Job Title*', dutyPlaceholder: 'Enter your job title',
          typeLabel: 'Institution Type*', typePlaceholder: 'Select your institution type',
          locationLabel: 'Location*', locationPlaceholder: 'Select your city',
          submit: 'Submit', submitAgain: 'Submit Again',
          cancel: 'Cancel and return to home page', returnHome: 'Back to Home',
          reviewDescription: 'Enter your registered email to view the status or update details.',
          pendingTitle: 'Application Under Review',
          pendingDescription: 'Your information has been submitted. We will complete the review within 2 business days. Please check your email for updates.',
          pendingContact: 'If you have any questions, please contact',
          approvedTitle: 'Application Approved',
          approvedDescription: 'Your trial has been approved. Details below are now read-only.',
          rejectedTitle: 'Application Not Approved',
          rejectedDescription: 'Please review the reason for rejection below, update the relevant information, and resubmit your application.',
          rejectReason: 'Reason for rejection',
          rejectReasonFallback: 'Please revise your information and submit the application again.',
          invalidEmail: 'Enter a valid business email', requiredCode: 'Enter the verification code',
          requiredName: 'Enter your full name', requiredInstitution: 'Enter your company/institution name',
          requiredDuty: 'Enter your job title', requiredType: 'Select your institution type',
          requiredLocation: 'Select your location', sent: 'Verification code sent'
        }
      }
      return {
        emailLabel: '企业邮箱*', emailPlaceholder: '请使用企业邮箱验证',
        codeLabel: '验证邮箱*', codePlaceholder: '请输入邮箱验证码',
        getCode: '获取验证码', continue: '继续',
        contactHint: '',
        nameLabel: '姓名*', namePlaceholder: '请输入您的姓名',
        institutionLabel: '机构名称*', institutionPlaceholder: '请输入您的企业/机构名称',
        dutyLabel: '职务*', dutyPlaceholder: '请输入您的职务',
        typeLabel: '机构类型*', typePlaceholder: '请选择您的机构类型',
        locationLabel: '所在地*', locationPlaceholder: '请输入您的所在城市',
        submit: '提交', submitAgain: '再次提交',
        cancel: '暂不提交，返回首页', returnHome: '返回首页',
        reviewDescription: '请验证申请时使用的邮箱，查看审核结果及未通过原因，并修改申请。',
        pendingTitle: '申请审核中',
        pendingDescription: '信息已提交，我们将在2个工作日内完成审核，请留意邮件通知。',
        pendingContact: '如有疑问，请联系',
        approvedTitle: '申请已通过',
        approvedDescription: '您的试用申请已通过，以下申请信息不可修改。',
        rejectedTitle: '申请未通过',
        rejectedDescription: '请查看下方未通过原因，修改相关信息后重新提交。',
        rejectReason: '未通过原因',
        rejectReasonFallback: '审核未通过，请修改申请信息后再次提交。',
        invalidEmail: '请输入正确的企业邮箱', requiredCode: '请输入邮箱验证码',
        requiredName: '请输入姓名', requiredInstitution: '请输入机构名称',
        requiredDuty: '请输入职务', requiredType: '请选择机构类型',
        requiredLocation: '请选择所在地', sent: '验证码已发送'
      }
    },
    benefits() {
      if (this.isEnglish) {
        return [
          { icon: researchIcon, title: 'Full Access to Research', description: 'Unlimited access to CF40 Research’s latest reports and insights.' },
          { icon: platformIcon, title: 'Multi-Platform Access', description: 'Available on PC, mobile app and WeChat Mini Program.' },
          { icon: advisoryIcon, title: 'Customized Advisory Services', description: 'Explore bespoke insights, tailored research, and expert consultations designed for clients.' }
        ]
      }
      return [
        { icon: researchIcon, title: '全量报告解锁', description: '阅读CF40研究原创报告，获取最新政策洞察。' },
        { icon: bilingualIcon, title: '双语产品接入', description: '体验中英双语产品，满足跨国团队研究需求。' },
        { icon: platformIcon, title: '多端协同体验', description: '支持PC、APP、微信小程序多端接入。' },
        { icon: advisoryIcon, title: '定制服务咨询', description: '了解专项研究、定制咨询等服务' }
      ]
    },
    institutionTypes() {
      return this.institutionTypeOptions.map(item => ({
        value: item.value,
        label: this.isEnglish ? (item.label_en || item.label) : item.label
      }))
    },
    readOnly() {
      return ['PENDING', 'APPROVED'].includes(this.applicationStatus)
    },
    isLoggedIn() {
      return Boolean(localStorage.getItem('cf40-token'))
    },
    statusClass() {
      return this.applicationStatus.toLowerCase()
    },
    statusTitle() {
      return {
        PENDING: this.copy.pendingTitle,
        APPROVED: this.copy.approvedTitle,
        REJECTED: this.copy.rejectedTitle
      }[this.applicationStatus] || ''
    },
    statusDescription() {
      return {
        PENDING: this.copy.pendingDescription,
        APPROVED: this.copy.approvedDescription,
        REJECTED: this.copy.rejectedDescription
      }[this.applicationStatus] || ''
    },
    emailRules() {
      return {
        verification_code: [{ required: true, message: this.copy.requiredCode, trigger: 'blur' }]
      }
    },
    applicationRules() {
      return {
        contact_name: [{ required: true, message: this.copy.requiredName, trigger: 'blur' }],
        institution_name: [{ required: true, message: this.copy.requiredInstitution, trigger: 'blur' }],
        contact_duty: [{ required: true, message: this.copy.requiredDuty, trigger: 'blur' }],
        institution_type: [{ required: true, message: this.copy.requiredType, trigger: 'change' }],
        location: [{ type: 'array', required: true, min: 1, message: this.copy.requiredLocation, trigger: 'change' }]
      }
    }
  },
  created() {
    locale.use(this.isEnglish ? enLocale : zhLocale)
    this.setPageSeo(
      this.isEnglish ? 'Institutional Free Trial' : '试用申请',
      this.isEnglish ? 'CF40 Research,institutional trial' : 'CF40研究,试用申请',
      this.copy.contactHint
    )
    this.loadRegions()
    this.loadInstitutionTypes()
    if (this.isLoggedIn) this.continueLoggedInApplication()
  },
  beforeDestroy() {
    if (this.countdownTimer) window.clearInterval(this.countdownTimer)
  },
  methods: {
    async openAgreement(type) {
      if (this.agreementLoading) return
      this.agreementLoading = true
      const privacy = type === 'privacy'
      const key = (privacy ? 'PRIVACY_CLAUSE' : 'MEMBERSHIP_SERVICE_AGREEMENT') + (this.isEnglish ? '_EN' : '')
      const title = this.isEnglish ? (privacy ? 'Privacy Policy' : 'Membership Service Agreement') : (privacy ? '隐私政策' : '会员服务协议')
      try {
        const res = await initConfig({}, this.isEnglish)
        if (!res || res.code !== 200) {
          this.$message.warning(this.isEnglish ? 'Unable to load the agreement. Please try again.' : '协议加载失败，请重试。')
          return
        }
        const item = (Array.isArray(res.data) ? res.data : []).find(item => item.config_key === key)
        if (item && item.config_value && item.config_value.trim()) {
          this.$refs.serviceAgreementRef.open(title, item.config_value)
        } else {
          this.$message.warning(this.isEnglish ? 'This agreement is not available yet. Please contact us.' : '协议暂未配置，请联系官方邮箱。')
        }
      } catch (error) {
        this.$message.warning(this.isEnglish ? 'Unable to load the agreement. Please try again.' : '协议加载失败，请重试。')
      } finally {
        this.agreementLoading = false
      }
    },
    async loadRegions() {
      const res = await institutionApplicationRegions(this.isEnglish)
      if (res && res.code === 200) {
        this.regionOptions = (res.data || []).map(item => this.toRegionOption(item, 0))
      }
    },
    async loadInstitutionTypes() {
      const res = await institutionApplicationTypes(this.isEnglish)
      if (res && res.code === 200) this.institutionTypeOptions = res.data || []
    },
    toRegionOption(item, depth) {
      let label = this.isEnglish ? (item.label_en || item.label) : item.label
      if (this.isEnglish && !item.label_en && depth === 0 && item.value && item.value.length === 2 && window.Intl && Intl.DisplayNames) {
        try {
          label = new Intl.DisplayNames(['en'], { type: 'region' }).of(item.value) || item.label
        } catch (e) {
          label = item.label
        }
      }
      const option = { label, value: item.label }
      if (Array.isArray(item.children) && item.children.length) {
        option.children = item.children.map(child => this.toRegionOption(child, depth + 1))
      }
      return option
    },
    async checkApplicationEmail() {
      const email = this.emailForm.email
      const fallback = this.isEnglish ? 'Unable to validate the email. Please try again.' : '邮箱校验失败，请稍后重试'
      try {
        const res = await validateInstitutionApplicationEmail({ email }, this.isEnglish)
        if (email !== this.emailForm.email) return false
        const result = res && res.code === 200 && res.data
        this.emailError = result && result.valid === true ? '' : (result && result.message) || fallback
        return !this.emailError
      } catch (error) {
        if (email === this.emailForm.email) this.emailError = fallback
        return false
      }
    },
    async sendCode() {
      if (this.sendingCode || this.verifying || this.countdown > 0) return
      this.sendingCode = true
      try {
        if (!await this.checkApplicationEmail()) return
        const res = await sendInstitutionApplicationCode({ email: this.emailForm.email }, this.isEnglish)
        if (res && res.code === 200) {
          this.$message.success(res.msg || this.copy.sent)
          this.startCountdown()
        } else if (res && res.msg) {
          this.$message.error(res.msg)
        }
      } catch (error) {
        // 请求层统一显示发码错误。
      } finally {
        this.sendingCode = false
      }
    },
    startCountdown() {
      this.countdown = 60
      if (this.countdownTimer) window.clearInterval(this.countdownTimer)
      this.countdownTimer = window.setInterval(() => {
        this.countdown -= 1
        if (this.countdown <= 0) {
          window.clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
      }, 1000)
    },
    async continueApplication() {
      if (this.verifying || this.sendingCode) return
      // 已填写验证码时必须验证当前表单，不能被登录账号的历史申请查询截走。
      if (!this.emailForm.verification_code && this.isLoggedIn && !this.requiresEmailVerification) {
        this.continueLoggedInApplication()
        return
      }
      this.verifying = true
      if (!await this.checkApplicationEmail()) {
        this.verifying = false
        return
      }
      this.$refs.emailForm.validate(async valid => {
        if (!valid) {
          this.verifying = false
          return
        }
        try {
          const res = await verifyInstitutionApplicationCode({
            email: this.emailForm.email,
            verification_code: this.emailForm.verification_code
          }, this.isEnglish)
          if (res && res.code === 200 && res.data && res.data.verification_token) {
            this.verificationToken = res.data.verification_token
            this.applyExistingApplication(res.data && res.data.application)
            this.step = 2
          } else {
            this.$message.error((res && res.code !== 200 && res.msg) || (this.isEnglish ? 'Email verification failed. Please try again.' : '邮箱验证失败，请重试。'))
          }
        } catch (error) {
          this.$message.error(this.isEnglish ? 'Unable to verify the email. Please try again.' : '邮箱验证请求失败，请重试。')
        } finally {
          this.verifying = false
        }
      })
    },
    async continueLoggedInApplication() {
      if (this.verifying) return
      this.verifying = true
      try {
        const res = await currentInstitutionApplication(this.isEnglish)
        if (res && res.code === 200) {
          this.emailForm.email = (res.data && res.data.email) || ''
          this.verificationToken = ''
          if (res.data && res.data.application) {
            this.applyExistingApplication(res.data.application)
            this.step = 2
          } else {
            this.applyExistingApplication(null)
            this.requiresEmailVerification = true
            this.$nextTick(() => {
              if (this.$refs.emailForm) this.$refs.emailForm.clearValidate()
            })
          }
        } else {
          this.$message.error((res && res.msg) || (this.isEnglish ? 'Unable to load your application. Please try again.' : '申请信息加载失败，请重试。'))
        }
      } catch (error) {
        this.$message.error(this.isEnglish ? 'Unable to load your application. Please try again.' : '申请信息加载失败，请重试。')
      } finally {
        this.verifying = false
      }
    },
    applyExistingApplication(application) {
      if (!application) {
        this.applicationStatus = ''
        this.reviewRemark = ''
        return
      }
      this.emailForm.email = application.email || this.emailForm.email
      this.applicationForm.contact_name = application.contact_name || ''
      this.applicationForm.institution_name = application.institution_name || ''
      this.applicationForm.contact_duty = application.contact_duty || ''
      this.applicationForm.institution_type = application.institution_type || ''
      this.applicationForm.country_region = application.country_region || ''
      this.applicationForm.province = application.province || ''
      this.applicationForm.city = application.city || ''
      this.applicationForm.location = [
        this.applicationForm.country_region,
        this.applicationForm.province,
        this.applicationForm.city
      ].filter(Boolean)
      this.applicationStatus = String(application.status || '').toUpperCase()
      this.reviewRemark = application.review_remark || ''
      this.$nextTick(() => {
        if (this.$refs.applicationForm) this.$refs.applicationForm.clearValidate()
      })
    },
    handleLocationChange(values) {
      const location = Array.isArray(values) ? values : []
      this.applicationForm.country_region = location[0] || ''
      this.applicationForm.province = location[1] || ''
      this.applicationForm.city = location[2] || ''
    },
    submitApplication() {
      if (this.submitting || this.readOnly) return
      if (!this.agreementsAccepted) {
        this.$message.warning(this.isEnglish ? 'Please read and agree to the Privacy Policy and Membership Service Agreement.' : '请先阅读并同意隐私条款和会员服务协议。')
        return
      }
      this.$refs.applicationForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          if (!await this.checkApplicationEmail()) {
            this.step = 1
            this.requiresEmailVerification = true
            this.verificationToken = ''
            return
          }
          const payload = {
            email: this.emailForm.email,
            verification_token: this.verificationToken,
            contact_name: this.applicationForm.contact_name,
            institution_name: this.applicationForm.institution_name,
            contact_duty: this.applicationForm.contact_duty,
            institution_type: this.applicationForm.institution_type,
            country_region: this.applicationForm.country_region,
            province: this.applicationForm.province,
            city: this.applicationForm.city
          }
          const res = await submitInstitutionApplication(payload, this.isEnglish)
          if (res && res.code === 200) {
            this.successVisible = true
            this.applicationStatus = 'PENDING'
            this.reviewRemark = ''
            window.scrollTo({ top: 0, behavior: 'smooth' })
          } else if (res && res.msg) {
            this.$message.error(res.msg)
          }
        } finally {
          this.submitting = false
        }
      })
    },
    goHome() {
      this.$router.push(this.homePath)
    }
  }
}
</script>

<style lang="scss" scoped>
@font-face {
  font-family: 'Application HarmonyOS';
  src: url('~@/assets/fonts/HarmonyOS_Sans_SC_Regular.ttf') format('truetype');
  font-display: swap;
}

@font-face {
  font-family: 'Application Roboto Light';
  src: url('~@/assets/fonts/Roboto-Light.ttf') format('truetype');
  font-display: swap;
}

.institution-application {
  --trial-orange: #ed6f33;
  position: relative;
  min-height: max(851px, 100vh);
  display: flex;
  flex-direction: column;
  color: #1e1e1e;
  background: #fff;
  font-family: 'Application HarmonyOS', sans-serif;

  &::before {
    position: absolute;
    inset: 0;
    content: '';
    pointer-events: none;
    background: url('~@/assets/institution-application/background.jpg') center / cover no-repeat;
    transform: scaleY(-1);
    opacity: .24;
  }

  button,
  input {
    font-family: inherit;
  }
}

.application-header,
.application-main,
.application-footer {
  position: relative;
}

.application-header {
  height: 132px;
  flex-shrink: 0;

  &-inner {
    position: relative;
    width: 1002px;
    height: 100%;
    margin: 0 auto;
  }
}

.application-logo {
  position: absolute;
  top: 71px;
  left: 0;
  display: block;
  line-height: 0;

  img {
    display: block;
    width: 205.51px;
    height: 36.64px;
  }
}

.application-slogan {
  position: absolute;
  top: 49px;
  right: 0;
  width: 537px;
  height: 75px;
  object-fit: contain;
}

.application-main {
  padding-top: 69px;
}

.application-content {
  width: 1002px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 491px 491px;
  column-gap: 20px;
  align-items: start;
}

.benefits-panel {
  padding-top: 92px;
}

.benefits-intro {
  margin: 0 0 50px;
  font-size: 16px;
  line-height: 21px;
  white-space: nowrap;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 13px;
  min-height: 24px;
  margin-bottom: 33px;
  color: #5a3118;
  font-size: 15px;
  line-height: 24px;

  .benefit-icon {
    display: block;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
  }

  strong {
    font-weight: 400;
  }

  p {
    display: inline;
  }
}

.chinese-page .benefit-item>div {
  white-space: nowrap;
}

.form-panel {
  position: relative;
  min-width: 0;

  h2 {
    position: absolute;
    top: -69px;
    width: 100%;
    color: var(--trial-orange);
    font-size: 32px;
    font-weight: 500;
    line-height: 42px;
    text-align: center;
  }
}

.application-card {
  min-height: 471px;
  padding: 14px 44px 15px 34px;
  box-sizing: border-box;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 52px rgba(190, 208, 227, .4);

  .required-mark {
    color: var(--trial-orange);
  }

  ::v-deep {
    .el-form-item {
      margin-bottom: 0;
    }

    .el-form-item__label {
      padding: 0;
      color: #1e1e1e;
      font-size: 13px;
      font-weight: 400;
      line-height: 33px;
    }

    .el-form-item__content {
      line-height: 38px;
    }

    .el-input {
      font-size: 12px;
      vertical-align: top;
    }

    .el-input__inner {
      height: 38px;
      padding: 0 12px;
      border: 1px solid #e9e9e9;
      border-radius: 8px;
      color: #1e1e1e;
      font-family: inherit;
      line-height: 38px;
      background: #fcfafa;

      &::placeholder {
        color: #bbb;
      }

      &:focus {
        border-color: var(--trial-orange);
      }
    }

    .el-input.is-disabled .el-input__inner {
      color: #999;
      background: #f5f5f5;
    }

    .el-select,
    .el-cascader {
      width: 100%;
      line-height: 38px;
      vertical-align: top;
    }

    .el-input__icon {
      line-height: 38px;
      color: #777;
    }

    .el-form-item__error {
      padding-top: 3px;
      font-size: 11px;
    }
  }
}

.step-1 .application-card {
  padding-right: 39px;
  padding-left: 39px;
}

.email-form {
  .el-form-item+.el-form-item {
    margin-top: 27px;
  }

  ::v-deep .el-input__inner {
    padding-left: 27px;
  }

  .primary-button {
    margin-top: 198px;
    transform: none;
    color: #fff;
    border-color: #ffdccb;
    background: #ffdccb;

    &.is-ready {
      border-color: var(--trial-orange);
      background: var(--trial-orange);
    }
  }
}

.code-field {
  position: relative;

  .el-input ::v-deep .el-input__inner {
    padding-right: 112px;
  }
}

.code-button {
  position: absolute;
  top: 1px;
  right: 1px;
  height: 36px;
  padding: 0 14px;
  border: 0;
  color: var(--trial-orange);
  font-size: 13px;
  cursor: pointer;
  background: transparent;

  &:disabled {
    color: #bbb;
    cursor: not-allowed;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 19px 33px;
}

.location-field {
  grid-column: 1 / -1;
}

.primary-button {
  display: block;
  width: 168px;
  height: 39px;
  padding: 0;
  margin: 93px auto 0;
  // 表单左右内边距依设计稿不对称，按钮对齐卡片中心。
  transform: translateX(5px);
  border: 1px solid var(--trial-orange);
  border-radius: 6px;
  color: #fff;
  font-size: 16px;
  font-weight: 400;
  background: var(--trial-orange);

  &:hover,
  &:focus {
    color: #fff;
    border-color: #f28754;
    background: #f28754;
  }
}

.form-hint {
  width: calc(100% + 78px);
  margin: 14px 0 0 -34px;
  color: #787878;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  white-space: nowrap;
}

.agreement-consent {
  margin-top: 21px;
  padding-top: 17px;
  border-top: 1px solid #e9e9e9;
  color: #777;
  font-size: 12px;
  line-height: 20px;
  overflow-wrap: anywhere;

  button {
    padding: 0;
    border: 0;
    color: var(--trial-orange);
    font: inherit;
    text-align: left;
    text-decoration: underline;
    cursor: pointer;
    background: transparent;
  }

  button:focus-visible {
    outline: 2px solid var(--trial-orange);
    outline-offset: 2px;
  }

  button:disabled {
    cursor: wait;
  }

  .agreement-loading {
    display: block;
    color: #777;
  }

  ::v-deep {
    .el-checkbox {
      margin-right: 3px;
      color: #777;
      font-size: 12px;
      font-weight: 400;
    }

    .el-checkbox__label {
      padding-left: 4px;
      font-size: 12px;
      line-height: 20px;
    }

    .el-checkbox__inner {
      width: 13px;
      height: 13px;
      border-radius: 0;
    }

    .el-checkbox__inner::after {
      top: 1px;
      left: 4px;
      width: 3px;
      height: 6px;
    }

    .el-checkbox__input.is-checked .el-checkbox__inner {
      border-color: var(--trial-orange);
      background: var(--trial-orange);
    }

    .el-checkbox__input.is-focus .el-checkbox__inner {
      border-color: var(--trial-orange);
    }

    .el-checkbox__input.is-checked+.el-checkbox__label {
      color: #777;
    }
  }
}

.cancel-link {
  display: block;
  // 设计稿：卡片左侧 60px、文字区域宽 362px，距卡片底部 17px。
  width: 362px;
  max-width: 100%;
  padding: 0;
  margin: 17px 0 0 60px;
  border: 0;
  color: #1a7786;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  background: transparent;

  &:hover {
    text-decoration: underline;
  }
}

.application-footer {
  width: min(1002px, calc(100% - 32px));
  min-height: 56px;
  box-sizing: border-box;
  margin: auto auto 0;
  padding-top: 76px;
  padding-bottom: 22px;
  color: #bbb;
  font-size: 10px;
  line-height: 14px;
  text-align: center;

  .footer-contact {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 0 14px;
  }

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: #1a7786;
      text-decoration: underline;
    }
  }

  p {
    margin-top: 6px;
  }
}

.status-panel {
  margin-bottom: 18px;
  padding: 12px 14px;
  border: 1px solid #d9e5ec;
  border-radius: 4px;
  background: #f5f9fb;

  strong {
    display: block;
    margin-bottom: 5px;
    color: #35434d;
    font-size: 16px;
  }

  p {
    color: #72808a;
    font-size: 13px;
    line-height: 1.65;
  }

  .contact-email {
    color: inherit;
    font-size: larger;
    font-weight: 700;
    text-decoration: underline;
    overflow-wrap: anywhere;
    -webkit-user-select: text;
    user-select: text;
    -webkit-touch-callout: default;
  }

  &.approved {
    border-color: #b8dfcd;
    background: #f0faf5;
  }

  &.approved strong {
    color: #198754;
  }

  &.rejected {
    border-color: #efc5c1;
    background: #fff6f5;
  }

  &.rejected strong {
    color: #c64f44;
  }
}

.reject-reason {
  margin-top: 22px;
  padding: 12px 14px;
  border-left: 3px solid #d65d50;
  background: #fff6f5;

  strong {
    color: #b7463c;
    font-size: 14px;
  }

  p {
    margin-top: 5px;
    color: #6f4a46;
    font-size: 13px;
    line-height: 1.65;
    overflow-wrap: anywhere;
  }
}

.has-status .primary-button {
  margin-top: 28px;
}

.english-page {
  .application-card ::v-deep .el-input__inner {
    font-family: 'Application Roboto Light', sans-serif;
  }

  .application-logo {
    top: 61px;
    left: 3px;

    img {
      width: 119px;
      height: 56px;
    }
  }

  .benefits-panel {
    padding: 14px 0 0 3px;
  }

  .benefits-panel h1 {
    margin-bottom: 43px;
    color: var(--trial-orange);
    font-size: 32px;
    font-weight: 500;
    line-height: 42px;
  }

  .benefit-item {
    position: relative;
    display: block;
    padding-left: 37px;
    margin-bottom: 28px;
    line-height: 28px;

    .benefit-icon {
      position: absolute;
      top: 1px;
      left: 0;
    }

    strong {
      font-weight: 500;
    }

    p {
      display: block;
      font-family: 'Application Roboto Light', sans-serif;
    }
  }

  &.step-2 {
    .benefits-panel {
      padding-left: 5px;
    }

    .benefits-panel h1 {
      margin-left: -2px;
    }
  }
}

::v-deep .application-success-modal {
  opacity: .299;
}

::v-deep .application-success-dialog {
  position: absolute;
  top: 50%;
  left: 50%;
  max-width: calc(100% - 32px);
  margin: 0 !important;
  transform: translate(-50%, -50%);
  border-radius: 6px;
  box-shadow: none;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 20px 18px;
    color: #1e1e1e;
    text-align: center;
  }

  .success-icon {
    display: block;
    width: 50px;
    height: 50px;
    margin: 0 auto 17px;
  }

  h3 {
    font-size: 18px;
    line-height: 22px;
    font-weight: 700;
  }

  p {
    max-width: 264px;
    margin: 20px auto 0;
    font-size: 13px;
    line-height: 20px;
  }

  .primary-button {
    margin-top: 20px;
    transform: none;
  }
}

.english-page ::v-deep .application-success-dialog .el-dialog__body {
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
}

.english-page ::v-deep .application-success-dialog p {
  max-width: 284px;
}

@media screen and (max-width: 1100px) {

  .application-header-inner,
  .application-content {
    width: calc(100% - 64px);
  }

  .application-content {
    grid-template-columns: minmax(0, 1fr) minmax(380px, 1fr);
    gap: 24px;
  }

  .benefits-intro,
  .chinese-page .benefit-item>div {
    white-space: normal;
  }

  .benefits-intro {
    line-height: 1.6;
  }

  .application-slogan {
    width: 470px;
    object-position: right center;
  }

  .application-card,
  .step-1 .application-card {
    padding-right: 24px;
    padding-left: 24px;
  }

  .primary-button {
    transform: none;
  }

  .form-hint {
    width: auto;
    margin-left: 0;
    white-space: normal;
  }

  .form-grid {
    column-gap: 18px;
  }

  .cancel-link {
    margin-right: auto;
    margin-left: auto;
  }
}

@media screen and (max-width: 767px) {
  .institution-application {
    min-height: 100vh;
  }

  .application-header {
    height: 100px;
  }

  .application-header-inner {
    width: calc(100% - 40px);
  }

  .application-logo {
    top: 34px;
  }

  .english-page .application-logo {
    top: 24px;
  }

  .application-slogan {
    display: none;
  }

  .application-main {
    padding-top: 24px;
  }

  .application-content {
    width: calc(100% - 32px);
    display: flex;
    flex-direction: column;
    gap: 30px;
  }

  .benefits-panel,
  .english-page .benefits-panel {
    padding: 0;
  }

  .benefits-intro,
  .english-page .benefits-panel h1 {
    margin-bottom: 24px;
  }

  .benefit-item {
    margin-bottom: 16px;
  }

  .form-panel {
    width: 100%;
  }

  .form-panel h2 {
    position: static;
    margin-bottom: 20px;
  }

  .application-card {
    min-height: 0;
    padding: 18px 20px 24px;
  }

  .form-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .email-form .primary-button,
  .details-form .primary-button {
    margin-top: 42px;
  }

  .form-hint {
    margin-top: 18px;
  }

  .application-footer {
    padding-top: 40px;
    padding-bottom: 20px;
  }
}
</style>
